import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CABINET_TYPE_DEFAULTS, ACCESSORIES, EXTRA_TYPES, type CabinetType } from "@/data/cabinetDefaults";
import ProjectProfitPanel from "./ProjectProfitPanel";

interface ContractorProject {
  id: string;
  name: string;
  client_name: string;
  status: string;
  wood_species: string;
  finish: string;
  door_style: string;
  notes: string;
  created_at: string;
  contractor_company?: string;
  charged_total: number | null;
  labor_hours: number | null;
  labor_rate: number | null;
}

interface GroupRow {
  id: string;
  name: string;
  wood_species: string;
  finish: string;
  door_style: string;
}

const AdminProjectsTab = () => {
  const [projects, setProjects] = useState<ContractorProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, { cabinets: any[]; extras: any[]; groups: GroupRow[] }>>({});

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*, contractor_profiles(company_name)")
      .order("updated_at", { ascending: false });

    if (error) console.error("Admin projects fetch error:", error);

    if (data) {
      setProjects(
        data.map((p: any) => ({
          ...p,
          contractor_company: p.contractor_profiles?.company_name || "Unknown",
        }))
      );
    }
    setLoading(false);
  };


  const toggleExpand = async (projectId: string) => {
    if (expandedId === projectId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(projectId);

    if (!details[projectId]) {
      const [cabRes, extRes, grpRes] = await Promise.all([
        supabase.from("project_cabinets").select("*").eq("project_id", projectId).order("sort_order"),
        supabase.from("project_extras").select("*").eq("project_id", projectId),
        supabase.from("project_groups").select("*").eq("project_id", projectId).order("sort_order"),
      ]);
      setDetails((prev) => ({
        ...prev,
        [projectId]: {
          cabinets: (cabRes.data || []).map((c: any) => ({ ...c, accessories: Array.isArray(c.accessories) ? c.accessories : [] })),
          extras: extRes.data || [],
          groups: (grpRes.data || []) as GroupRow[],
        },
      }));
    }
  };

  const handleMarkQuoted = async (projectId: string) => {
    await supabase.from("projects").update({ status: "quoted" }).eq("id", projectId);
    fetchProjects();
  };

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading projects...</p>;
  }

  if (projects.length === 0) {
    return <p className="text-muted-foreground text-sm">No submitted projects yet.</p>;
  }

  const renderCabinetLine = (cab: any) => {
    const typeLabel = CABINET_TYPE_DEFAULTS[cab.cabinet_type as CabinetType]?.label || cab.cabinet_type;
    const accLabels = (cab.accessories || [])
      .map((id: string) => ACCESSORIES.find((a) => a.id === id)?.label)
      .filter(Boolean);
    return (
      <div key={cab.id} className="text-sm text-foreground py-1">
        {cab.quantity > 1 && <span className="text-primary font-medium">{cab.quantity}× </span>}
        {typeLabel} — {cab.width_inches}" × {cab.height_inches}" × {cab.depth_inches}"
        {cab.label && <span className="text-muted-foreground"> ({cab.label})</span>}
        {accLabels.length > 0 && (
          <span className="text-muted-foreground text-xs ml-2">[{accLabels.join(", ")}]</span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {projects.map((project) => {
        const isExpanded = expandedId === project.id;
        const detail = details[project.id];
        return (
          <div key={project.id} className="border border-border">
            <div
              className="p-4 flex items-start justify-between gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => toggleExpand(project.id)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground break-words">{project.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    project.status === "submitted"
                      ? "bg-primary/10 text-primary"
                      : project.status === "quoted"
                      ? "bg-green-100 text-green-800"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {project.status}
                  </span>

                </div>
                <p className="text-sm text-muted-foreground break-words">
                  {project.contractor_company}
                  {project.client_name && ` · ${project.client_name}`}
                  {project.wood_species && ` · ${project.wood_species}`}
                  {project.door_style && ` · ${project.door_style}`}
                </p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                {new Date(project.created_at).toLocaleDateString()}
              </span>
            </div>

            {isExpanded && detail && (
              <div className="border-t border-border p-4 bg-muted/10 space-y-4">
                {/* Grouped cabinets */}
                {detail.groups.length > 0 ? (
                  <>
                    {detail.groups.map((group) => {
                      const groupCabs = detail.cabinets.filter((c: any) => c.group_id === group.id);
                      const materialParts = [
                        group.wood_species || project.wood_species,
                        group.finish || project.finish,
                        group.door_style || project.door_style,
                      ].filter(Boolean);
                      return (
                        <div key={group.id}>
                          <h4 className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-1">
                            {group.name} ({groupCabs.reduce((s: number, c: any) => s + (c.quantity || 1), 0)})
                          </h4>
                          {materialParts.length > 0 && (
                            <p className="text-xs text-muted-foreground mb-2 italic">{materialParts.join(" · ")}</p>
                          )}
                          {groupCabs.map(renderCabinetLine)}
                          {groupCabs.length === 0 && (
                            <p className="text-xs text-muted-foreground italic">No cabinets</p>
                          )}
                        </div>
                      );
                    })}
                    {/* Ungrouped */}
                    {(() => {
                      const ungrouped = detail.cabinets.filter((c: any) => !c.group_id);
                      if (ungrouped.length === 0) return null;
                      return (
                        <div>
                          <h4 className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-1">
                            General ({ungrouped.reduce((s: number, c: any) => s + (c.quantity || 1), 0)})
                          </h4>
                          {ungrouped.map(renderCabinetLine)}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div>
                    <h4 className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">
                      Cabinets ({detail.cabinets.reduce((s: number, c: any) => s + (c.quantity || 1), 0)})
                    </h4>
                    {detail.cabinets.map(renderCabinetLine)}
                  </div>
                )}

                {/* Extras */}
                {detail.extras.length > 0 && (
                  <div>
                    <h4 className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">Extras</h4>
                    {detail.extras.map((ext: any) => {
                      const def = EXTRA_TYPES.find((e) => e.id === ext.extra_type);
                      return (
                        <div key={ext.id} className="text-sm text-foreground py-1">
                          {def?.label || ext.extra_type}: {ext.quantity} {def?.unit || ""}
                          {ext.notes && <span className="text-muted-foreground"> — {ext.notes}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Notes */}
                {project.notes && (
                  <div>
                    <h4 className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-1">Notes</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{project.notes}</p>
                  </div>
                )}

                {project.status === "submitted" && (
                  <button
                    onClick={() => handleMarkQuoted(project.id)}
                    className="text-xs text-primary underline hover:text-primary/80"
                  >
                    Mark as Quoted
                  </button>
                )}

                <ProjectProfitPanel
                  projectId={project.id}
                  initialChargedTotal={project.charged_total}
                  initialLaborHours={project.labor_hours}
                  initialLaborRate={project.labor_rate}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminProjectsTab;
